import { inject } from '../di'
import {
    BaseAnalyticsDelegate,
    AnalyticsParameters,
} from './BaseAnalyticsDelegate'
import {
    IAnalyticsService,
    Dimension,
    TRACKER_PARTNER,
    AggregateSourceAction,
    TrackFlippProps,
} from '../definitions/IAnalyticsService'
import { ConfigService, DistributionMode, Experience } from '../ConfigService'
import { Engagement, EVTrackingService } from '../EVTrackingService'
import { EventService } from '../EventService'
import { parseSearchParams } from '../../utils/URL'
import { Flyer, TrackingUrl } from '../../definitions/FlyerDefinitions'
import {
    PostMessageEvent,
    PageStatus,
} from '../../definitions/CommonDefinitions'
import AvroAnalytics from './HeroAvro'

const MESSAGE_APP_READY = 'ready_for_analytics'
const MESSAGE_FOREGROUND_ENABLED = 'enable_analytics'
const EVENT_TYPES: any = {
    engagement: 1,
    ev: 1,
    open: 2,
    module_impression: 3,
    module_viewed: 4,
    impression: 5,
}

const RANDOM_PLACE_HOLDER = /\[\[randomn?\]\]/gi
const PUBID_PLACE_HOLDER = /\[\[pubid\]\]/i
const NON_ALPHANUMERIC = /\W/

enum LocationSource {
    UNKNOWN, // 0
    USER_SUPPLIED, // 1
    GEOIP, // 2
    DEFAULT, // 3
    AUTO_LOCATE, // 4
}

export enum ClientType {
    DESKTOP = 1,
    TABLET_WEB = 2,
    TABLET_IOS = 3,
    MOBILE_WEB = 4,
    MOBILE_IOS = 5,
}

export class DistAnalyticsDelegate
    extends BaseAnalyticsDelegate
    implements IAnalyticsService
{
    @inject config: ConfigService
    @inject evTracker: EVTrackingService
    @inject eventService: EventService

    private avro: AvroAnalytics = new AvroAnalytics()
    // workaround for beacon HeroPostOpenPostExperience
    private web_open: boolean = false
    private _analyticsParameters: AnalyticsParameters = {}
    private _analyticsDelayed = false
    private _isBackgroundMode = false
    private _isWebGLSent = false // Boolean to track whether or not we've already sent the WebGL beacon
    private _pagesViewed = 0
    private _profileId: string
    // flyer_billing is a parameter that indicates if the flyer that was sent from Distribution
    // should be billed or not.
    private _flyer_billing = true
    private _cross_browse_billing = true
    private _origin_flyer: string
    private _source_channel: string = 'FlippWeb1'

    private _delayedEVTracking: { [key: string]: number[] } = {
        open: [],
        ev: [],
    }

    constructor() {
        super()
        /* Initialize the partner GA tracker based on the profile passed in provided */
        const search = parseSearchParams(new URL(window.location.href))
        this._profileId = search['ga_profile']
        if (this._profileId) {
            /* Initialize the partner GA tracker */
            ga('create', this._profileId, 'auto', TRACKER_PARTNER)
            ga('set', 'forceSSL', true)
            // ga("set", "dimension5", this.config.BUILD);
        }

        this.setSessionIdInGTM()
        this.setSourceChannel()

        this._buildAnalyticsParameters()
    }

    setFunctionalVariables(search: { [key: string]: string }) {
        if (search['background_mode']) {
            this._isBackgroundMode = search['background_mode'] === 'true'
        }
        if (this._isBackgroundMode) {
            this._analyticsDelayed = true
        }

        if (search['flyer_billing'] === 'false') {
            this._flyer_billing = false
        }
        if (search['cross_browse_billing'] === 'false') {
            this._cross_browse_billing = false
        }
        if (search['flyer_id'] != null) {
            this._origin_flyer = search['flyer_id']
        }
    }

    analyticsParameters() {
        const params: { [key: string]: string } = {}
        Object.getOwnPropertyNames(this._analyticsParameters).forEach((key) => {
            params[key] = this._analyticsParameters[key]
        })

        params['publisher_id'] = this.config.publisherId
        params['postal_code'] = this.postalCode
        params['channel_id'] = this.channelId.toString()
        params['rnd'] = Math.random().toString().slice(2)
        params['app_version'] = this.config.BUILD
        return params
    }

    isAnalyticsDelayed() {
        return this._analyticsDelayed
    }

    get channelId() {
        return Number(this._analyticsParameters['channel_id'])
    }

    get profileId() {
        return this._profileId
    }

    originFlyerId() {
        return this._origin_flyer
    }

    trackFlyerOpen(flyer: Flyer, sourceAction: AggregateSourceAction) {
        super.trackFlyerOpen(flyer, sourceAction)
        const id = flyer.id.toString()
        this.evTracker.savelastVisitedFlyer(flyer)
        this.evTracker.savelastVisitedFlyerSourceAction(sourceAction)
        this.setSourceChannel()

        const evenType = 'open'
        const options = this.combineFlyerAnalytics(flyer, {
            flyer_id: id,
            flyer_run_id: flyer.flyerRunId.toString(),
            flyer_type_id: flyer.flyerTypeId.toString(),
            publisher_id: this.config.publisherId,
            merchant_id: flyer.merchantId.toString(),
            premium_merchant: flyer.premium ? 'true' : 'false',
            repeat: this.evTracker.hasEngaged(flyer.id, evenType) ? '1' : '0',
            source_action: sourceAction,
            SourceChannel: this._source_channel,
        })

        const trackFlippProps: TrackFlippProps = {
            ev: evenType,
            options,
            addAvroKey: this.config.aidHero,
            isAvro: true,
        }
        this.trackFlipp(trackFlippProps)
        this.track3rdPartyBeacon(evenType, flyer)
        if (this.config.aidHero) this.avro.FireHeroPostOpenFlyerBeacon(options)

        // workaround for web_open beacon
        // fire only once when post open
        if (!this.web_open) {
            this.web_open = true
            if (this.config.aidHero)
                this.avro.FireHeroPostOpenPostExperienceBeacon(options)
        }

        this.trackGA(
            'send',
            {
                hitType: 'event',
                eventCategory: 'Flyer',
                eventAction: evenType,
                eventLabel: flyer.merchant,
                [Dimension.FlyerStatus.toString()]:
                    this.evTracker.engagementStatus(flyer),
                [Dimension.MerchantName.toString()]: flyer.merchant,
                [Dimension.MerchantId.toString()]: flyer.merchantId.toString(),
                [Dimension.FlyerId.toString()]: id,
                nonInteraction: 1,
            },
            TRACKER_PARTNER
        )

        /* Track the open if analytics are not delayed, otherwise track the entry locally until
         * we get the 'enable_analytics' event */
        if (!this.isAnalyticsDelayed()) {
            this.evTracker.setEngagement(flyer.id, evenType)
        } else {
            this._delayedEVTracking[evenType].push(flyer.id)
        }
    }

    trackFlyerEngagement(flyer: Flyer, sourceAction: AggregateSourceAction) {
        super.trackFlyerEngagement(flyer, sourceAction)
        const id = flyer.id.toString()
        const eventType = 'ev'
        const repeat = this.evTracker.hasEngaged(flyer.id, eventType)
        const status = this.evTracker.engagementStatus(flyer)
        const options = this.combineFlyerAnalytics(flyer, {
            flyer_id: id,
            flyer_run_id: flyer.flyerRunId.toString(),
            flyer_type_id: flyer.flyerTypeId.toString(),
            publisher_id: this.config.publisherId,
            merchant_id: flyer.merchantId.toString(),
            premium_merchant: flyer.premium ? 'true' : 'false',
            repeat: repeat ? '1' : '0',
            merchant: flyer.merchant,
            source_action: sourceAction,
            SourceChannel: this._source_channel,
        })

        if (this.config.aidHero)
            this.avro.FireHeroPostFlyerEngagedVisitFlyerBeacon(options)

        if (!repeat && this._shouldFireDistEv(flyer)) {
            /* Don't fire repeat EV a.wishabi beacons. See FW-790 */
            const updatedOptions = this._prepareNonBillableParams(
                flyer,
                options
            )
            const trackFlippProps: TrackFlippProps = {
                ev: eventType,
                options: updatedOptions,
                addAvroKey: this.config.aidHero,
                isAvro: true,
            }
            this.trackFlipp(trackFlippProps)
            this.track3rdPartyBeacon(eventType, flyer)
        }

        this.trackGA(
            'send',
            {
                hitType: 'event',
                eventCategory: 'Flyer',
                eventAction: 'engagement',
                eventLabel: flyer.merchant,
                eventValue: status === 'billable' ? 1 : 0,
                [Dimension.FlyerStatus.toString()]: status,
                [Dimension.MerchantName.toString()]: flyer.merchant,
                [Dimension.MerchantId.toString()]: flyer.merchantId.toString(),
                [Dimension.FlyerId.toString()]: id,
            },
            TRACKER_PARTNER
        )

        /* Track the EV if analytics are not delayed, otherwise track the entry locally until
         * we get the 'enable_analytics' event */
        if (!this.isAnalyticsDelayed()) {
            this.evTracker.setEngagement(flyer.id, eventType)
        } else {
            this._delayedEVTracking[eventType].push(flyer.id)
        }
    }

    trackFlyerView(
        flyer: Flyer,
        left: number,
        top: number,
        width: number,
        height: number,
        zoomScale: number,
        interaction: number
    ) {
        super.trackFlyerView(
            flyer,
            left,
            top,
            width,
            height,
            zoomScale,
            interaction
        )
        // The "view" event coordinate system is *weird*, internally we use
        // standard top,left screen coordinates, where moving down is *increasing*
        // the "top" coordinate, and moving right is *increasing* the left
        // coordinate.
        // NOTE: The "left" and "top" parameters passed into this function,
        // describe the top, left origin of the viewport *on the flyer*.
        // ```
        //   0,0
        //    *-------
        //    |
        //    | (3, 3)   (3, 13)
        //    |   *--------*
        //    |   |viewport|
        //    |   |        |
        //    |   *--------*
        //    | (6, 3)   (6, 13)
        // ```
        // The "view" event expects top,left cartesian coordinates. So in the
        // above example, the (top, left, bottom, right) is (-3, 3, -6, 13)
        // respectively.
        const id = flyer.id.toString()
        const options = {
            flyer_id: id,
            flyer_run_id: flyer.flyerRunId.toString(),
            flyer_type_id: flyer.flyerTypeId.toString(),
            publisher_id: this.config.publisherId,
            merchant_id: flyer.merchantId.toString(),
            premium_merchant: flyer.premium ? 'true' : 'false',
            left: left.toString(),
            top: (-top).toString(),
            right: (left + width).toString(),
            bottom: (-(top + height)).toString(),
            resolution: (1 / zoomScale).toString(),
            userScreenHeight: window.screen.availHeight.toString(),
            userScreenWidth: window.screen.availWidth.toString(),
        }

        const trackFlippProps: TrackFlippProps = {
            ev: 'view',
            options,
            addAvroKey: this.config.aidHero,
            isAvro: true,
        }
        this.trackFlipp(trackFlippProps)
        if (this.config.aidHero)
            this.avro.FireHeroPostFlyerViewFlyerBeacon(options)
    }

    trackPageView() {
        super.trackPageView()
        /* This function is called every time a page transition occurs (including on initial load).
         * For distribution, we want to sent the "ready" post message after the page has rendered.
         * Here, keep track of the number of times a page transition has occured and setup the postMessage
         * the first time. */
        if (this._pagesViewed === 0 && this._isBackgroundMode) {
            const receiveMessage = (evt: PostMessageEvent) => {
                // console.log("[FW] Got message", evt);
                if (evt.data === MESSAGE_FOREGROUND_ENABLED) {
                    this._analyticsDelayed = false
                    /* Fire event to notify other components that foreground is enabled (eg Flyer engagement timer) */
                    this.eventService.fireAnalyticsEvent('delayedAnalytics')

                    /* Fire the queued events (if any). Only do this if the Page is loaded, otherwise
                     * it will be deferred until the load callback */
                    if (flipp.status === PageStatus.Loaded) {
                        this.sendQueuedEvents()
                    }

                    /* Track the delayed engagement */
                    Object.getOwnPropertyNames(this._delayedEVTracking).forEach(
                        (type) => {
                            const ids = this._delayedEVTracking[type]
                            ids.forEach((id) => {
                                this.evTracker.setEngagement(
                                    id,
                                    type as Engagement
                                )
                            })
                        }
                    )
                    this._delayedEVTracking = {
                        open: [],
                        ev: [],
                    }

                    /* Piggyback off the 'enable_analytics' message post. Modules 3.0 will only send this postMessage
                     * to FW if we receive the 'ready_for_analytics' msg AND the pop overlay is open. */
                    const trackFlippProps: TrackFlippProps = {
                        ev: 'web_visible',
                        options: this.analyticsParameters(),
                        addAvroKey: this.config.aidHero,
                    }
                    this.trackFlipp(trackFlippProps)

                    window.removeEventListener('message', receiveMessage)
                }
            }
            window.addEventListener('message', receiveMessage)
            // console.log("[FW] Sending 'ready_for_post_message'");
            window.parent.postMessage(MESSAGE_APP_READY, '*')
        } else if (this._pagesViewed === 0) {
            /* If background mode is false, then we can just blindly fire the 'web_visible' beacon */
            const trackFlippProps: TrackFlippProps = {
                ev: 'web_visible',
                options: this.analyticsParameters(),
                addAvroKey: this.config.aidHero,
            }
            this.trackFlipp(trackFlippProps)
        }
        this._pagesViewed++
    }

    trackFlyerItem(flyer: Flyer, itemId: number, st: number) {
        super.trackFlyerItem(flyer, itemId, st)

        if (st !== 0) {
            /* st = 0 is "open"
             * For distribution, we only want to track item opens. All other event types
             * should be ignored */
            return
        }

        const id = flyer.id.toString()
        const options: any = {
            flyer_id: id,
            flyer_run_id: flyer.flyerRunId.toString(),
            flyer_type_id: flyer.flyerTypeId.toString(),
            flyer_item_id: itemId.toString(),
            publisher_id: this.config.publisherId,
            merchant_id: flyer.merchantId.toString(),
            premium_merchant: flyer.premium ? 'true' : 'false',
            store_id: null,
            ciab: 'false',
            st: 'info',
        }

        const trackFlippProps: TrackFlippProps = {
            ev: 'click',
            options,
            addAvroKey: this.config.aidHero,
            isAvro: true,
        }
        this.trackFlipp(trackFlippProps)
        if (this.config.aidHero)
            this.avro.FireHeroPostFlyerClickFlyerItemBeacon(options)
    }

    trackWebGLDisabled() {
        if (!this._isWebGLSent) {
            const trackFlippProps: TrackFlippProps = {
                ev: 'error',
                options: {
                    source: 'webgl_unsupported',
                },
                addAvroKey: this.config.aidHero,
            }
            this.trackFlipp(trackFlippProps)
            this._isWebGLSent = true
        }
    }

    trackWindowClose() {
        super.trackWindowClose()
        const flyer = this.evTracker.getlastVisitedFlyer()
        const sourceAction = this.evTracker.getlastVisitedFlyerSourceAction()
        if (flyer) {
            this.trackFlyerClose(flyer, sourceAction)
        }
    }

    trackFlyerClose(flyer: Flyer, sourceAction: AggregateSourceAction) {
        super.trackFlyerClose(flyer, sourceAction)
        if (flyer) {
            const options = this.combineFlyerAnalytics(flyer, {
                flyer_id: flyer.id.toString(),
                flyer_run_id: flyer.flyerRunId.toString(),
                flyer_type_id: flyer.flyerTypeId.toString(),
                publisher_id: this.config.publisherId,
                merchant_id: flyer.merchantId.toString(),
                premium_merchant: flyer.premium ? 'true' : 'false',
                repeat: this.evTracker.hasEngaged(flyer.id, 'open') ? '1' : '0',
                source_action: sourceAction,
            })
            // workaround if we have flyer we send data, otherwise we dont have any data about flyer to send it
            if (this.config.aidHero)
                this.avro.FireHeroPostClosePostBeacon(options)

            const trackFlippProps: TrackFlippProps = {
                ev: 'close',
                options,
                syncGetRequest: true,
                addAvroKey: this.config.aidHero,
                isAvro: true,
            }
            this.trackFlipp(trackFlippProps)
        }
    }

    private _buildAnalyticsParameters(): { [key: string]: string } {
        const search = parseSearchParams(new URL(window.location.href))
        const params: { [key: string]: string } = (this._analyticsParameters = {
            aid: search['aid'] || 'flyers',
            location: 'flippweb',
            sid: this.sessionId,
            client_type: bowser.tablet
                ? `${ClientType.TABLET_WEB}`
                : bowser.mobile
                  ? `${ClientType.MOBILE_WEB}`
                  : `${ClientType.DESKTOP}`,
        })

        if (search['background_mode']) {
            this._isBackgroundMode = search['background_mode'] === 'true'
        }
        if (this._isBackgroundMode) {
            this._analyticsDelayed = true
        }

        // If FORCE_NATIVE is true, it means there are no query parameters in the URL so need to get those values
        // from the cookie if available.
        const CDPCookieJSON = Cookies.getJSON('cdp-parameters')
        if (this.config.FORCE_NATIVE && CDPCookieJSON) {
            if (CDPCookieJSON['channel_id'])
                params['channel_id'] = CDPCookieJSON['channel_id']
            if (CDPCookieJSON['traffic_source'])
                params['traffic_source'] = CDPCookieJSON['traffic_source']
        } else {
            if (search['channel_id'])
                params['channel_id'] = search['channel_id']
            if (search['traffic_source'])
                params['traffic_source'] = search['traffic_source']
        }

        if (search['utm_content']) params['utm_content'] = search['utm_content']
        if (search['utm_source']) params['utm_source'] = search['utm_source']
        if (search['utm_medium']) params['utm_medium'] = search['utm_medium']
        if (search['utm_term']) params['utm_term'] = search['utm_term']
        if (search['utm_campaign'])
            params['utm_campaign'] = search['utm_campaign']

        /* Read 'location_source' from param, otherwise assume Default */
        if (search['location_source']) {
            params['location_source'] = search['location_source']
        } else {
            params['location_source'] = `${LocationSource.DEFAULT}`
        }

        return params
    }

    private _shouldFireDistEv(flyer: Flyer) {
        return this._isOriginalFlyer(flyer) || this._cross_browse_billing
    }

    private _prepareNonBillableParams(
        flyer: Flyer,
        prevOptions: { [key: string]: string }
    ) {
        const updatedOptions = prevOptions
        if (this._isOriginalFlyer(flyer) && !this._flyer_billing) {
            updatedOptions['premium_merchant'] = 'false'
            updatedOptions['analytics_payload'] = ''
        }
        return updatedOptions
    }

    private _isOriginalFlyer(flyer: Flyer) {
        return this._origin_flyer === flyer.id.toString()
    }

    private track3rdPartyBeacon(eventType: any, flyer: Flyer) {
        if (!flyer.hasOwnProperty('trackingUrls')) {
            return
        }

        const trackingUrl = flyer.trackingUrls.filter(
            (trackingUrl: TrackingUrl) => {
                return (
                    trackingUrl.hasOwnProperty('eventType') &&
                    trackingUrl.eventType === EVENT_TYPES[eventType]
                )
            }
        )[0]

        if (!trackingUrl || !trackingUrl.hasOwnProperty('url')) {
            return
        }

        let url = trackingUrl.url

        url = this.addRandomPlaceholderIfAbsent(url)

        url = this.replaceAndEncode(url, [
            {
                from: RANDOM_PLACE_HOLDER,
                to: this.getRandomString(),
            },
            {
                from: PUBID_PLACE_HOLDER,
                to: `${flyer.id},${flyer.flyerRunId}`,
            },
        ])

        new Image().src = url
    }

    private getRandomString() {
        return Math.random().toString(36).replace(NON_ALPHANUMERIC, '')
    }

    private replaceAndEncode(url: string, replacements: any[]) {
        replacements.forEach((replacement: any) => {
            url = url.replace(replacement.from, replacement.to)
        })

        return encodeURI(url)
    }

    private addRandomPlaceholderIfAbsent(url: string) {
        if (!RANDOM_PLACE_HOLDER.test(url)) {
            url = url.concat('&[[randomn]]')
        }
        return url
    }

    // traffic source might be: 1)"CDP3", 2)"Module3", 3) "Hero1", 4) "FlippWeb1"
    // for cdp3.0 #1, for native modules #2 & #3, for flipp.com and other #4
    private setSourceChannel() {
        // all this checks we need because we save trafficSource to cookie and if we have it in cookie we pass it to req.params
        // so req.params not enough to identify cdp3.0 and dist (module templates) mode

        const moduleMod =
            this.config.DISTRIBUTION_MODE === DistributionMode.MODULE
        const distExp = this.config.EXPERIENCE === Experience.DISTRIBUTION
        if (moduleMod && distExp) {
            this._source_channel = this.config.aidHero ? 'Hero1' : 'Module3'
        } else {
            try {
                // if we have access to window.top that means we on flipp.com
                window.top.location.href
            } catch (e) {
                // if we dont have access to window.top that means we in iframe
                // thus it might be cdp3.0 or dist mode (module templates mode)
                // dist mode (module templates mode) we checked earlier (if condition above)
                // thus we in cdp3.0 mode
                this._source_channel = 'CDP3'
            }
        }
    }
}
